import { createAction } from '@reduxjs/toolkit';

import type {
    AvatarVoicePresetSelectionModalContext,
    ClipPreviewModalContext,
    CreateBlueprintFromVideoDashboardModalContext,
    CreateBlueprintModalContext,
    CreateFolderModalContext,
    CreateSimilarVideoModalContext,
    CropModalContext,
    DeleteFolderModalContext,
    DeletePronunciationModalContext,
    EditPronunciationModalContext,
    FeatureLockModalContext,
    GenerativeMediaDetailsModalContext,
    GenericErrorModalContext,
    InsertTrimModalContext,
    MediaDetailsModalContext,
    MODAL_IDS,
    NarrationStyleModalContext,
    RecordYourScreenModalContext,
    RecordYourselfModalContext,
    RenameFolderModalContext,
    SketchBlueprintsModalContext,
    SoundEffectSelectionModalContext,
    TemplatePickerModalContext,
    TranscriptGeneratedModalContext,
    TrimModalContext,
    UploadModalContext,
    UseCasePreviewModalContext,
    UserDropdownModalContext,
    VideoNameEditingModalContext,
    VoiceCloningModalContext,
    VoicedrivenSceneCreationModalContext,
    VoiceEditModalContext,
    VoiceSelectionModalContext,
} from 'js/src/data/modals/modals-types';
import { getTopModalId } from 'js/src/data/modals/modals-selectors';
import { MODALS_ACTIONS } from 'js/src/data/modals/modals-types';
import type { AppDispatch, RootState } from 'js/src/libs/RootState';

/**
 * Pushes the modal to show onto the top of the stack, along with any custom data
 *
 * @param modal_id one constant from MODAL_IDs
 * @param modal_context optional data that should be made available to the visible modal
 */
export interface ShowModalPayload {
    modal_id: MODAL_IDS;
    modal_context?:
        | Readonly<UploadModalContext>
        | Readonly<FeatureLockModalContext>
        | Readonly<TranscriptGeneratedModalContext>
        | Readonly<EditPronunciationModalContext>
        | Readonly<MediaDetailsModalContext>
        | Readonly<GenerativeMediaDetailsModalContext>
        | Readonly<GenericErrorModalContext>
        | Readonly<VoicedrivenSceneCreationModalContext>
        | Readonly<CropModalContext>
        | Readonly<TrimModalContext>
        | Readonly<InsertTrimModalContext>
        | Readonly<UserDropdownModalContext>
        | Readonly<VoiceSelectionModalContext>
        | Readonly<AvatarVoicePresetSelectionModalContext>
        | Readonly<RecordYourselfModalContext>
        | Readonly<RecordYourScreenModalContext>
        | Readonly<DeletePronunciationModalContext>
        | Readonly<CreateSimilarVideoModalContext>
        | Readonly<CreateBlueprintFromVideoDashboardModalContext>
        | Readonly<ClipPreviewModalContext>
        | Readonly<TemplatePickerModalContext>
        | Readonly<SketchBlueprintsModalContext>
        | Readonly<VideoNameEditingModalContext>
        | Readonly<CreateBlueprintModalContext>
        | Readonly<RenameFolderModalContext>
        | Readonly<DeleteFolderModalContext>
        | Readonly<CreateFolderModalContext>
        | Readonly<SoundEffectSelectionModalContext>
        | Readonly<VoiceCloningModalContext>
        | Readonly<VoiceEditModalContext>
        | Readonly<UseCasePreviewModalContext>
        | Readonly<NarrationStyleModalContext>;
}
export const showModal = createAction<ShowModalPayload>(MODALS_ACTIONS.SHOW_MODAL);

export const hideModal = createAction<undefined>(MODALS_ACTIONS.HIDE_MODAL);

/**
 * Show a modal only if it isn't already at the top of the stack. Because the modal state is a stack
 * that {@link showModal} pushes onto, many near-simultaneous callers opening the same modal (e.g. a
 * batch of parallel requests all failing with the same feature-limit error) would otherwise stack
 * duplicate copies the user has to dismiss one by one. This thunk runs synchronously, so within such
 * a burst the first push is visible to the rest and they no-op.
 */
export const showModalIfNotAtTop =
    (payload: ShowModalPayload) => (dispatch: AppDispatch, getState: () => RootState) => {
        if (getTopModalId(getState()) === payload.modal_id) {
            return;
        }
        dispatch(showModal(payload));
    };
